import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { amplifyInitializer } from 'src/app/@configs/amplify-init';
import { appEventConfigInitializer } from 'src/app/@configs/app-event-config.init';
import { appIconsInitializer } from 'src/app/@configs/app-icons.init';
import { appMixpanelInitializer } from 'src/app/@configs/app-mixpanel.init';
import { appRouteTrackingInitializer } from 'src/app/@configs/app-route-tracking.init';
import { appScrollTrackingInitializer } from 'src/app/@configs/app-scroll-tracking.init';
import { provideTranslocoConfig } from 'src/app/@configs/transloco.config';
import { AuthSessionService } from 'src/app/@core/auth/services/auth-session';
import { AuthTokenService } from 'src/app/@core/auth/services/auth-token';
import { authInterceptor } from 'src/app/@interceptors/auth.interceptor';
import { showWinnersAuthInterceptor } from 'src/app/@networking/core/auth.interceptor';
import { NetworkingSessionService } from 'src/app/@services/networking-session.service';
import { SessionService } from 'src/app/@networking/core/session.service';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';
import { firstValueFrom } from 'rxjs';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    importProvidersFrom(
      BrowserModule,
      MatIconModule,
      FormsModule,
      MatSnackBarModule
    ),
    provideHttpClient(
      withInterceptors([authInterceptor, showWinnersAuthInterceptor])
    ),
    provideAnimations(),
    provideAppInitializer(() => {
      const initializerFn = amplifyInitializer();
      return initializerFn();
    }),
    // Re-establish the show-winners identity on EVERY entry point
    // deep link or refresh straight onto /network or /profile, where
    // HomeComponent never runs — from the enrolled email in localStorage, so
    // backend calls always carry x-user-id / x-user-email. HomeComponent refines
    // enriched-state on an actual login. Also pulls @networking/core into the
    // compile graph.
    provideAppInitializer(() => {
      inject(SessionService).syncFromEnrollReport();
    }),
    provideAppInitializer(() => {
      const initializerFn = appEventConfigInitializer();
      return initializerFn();
    }),
    provideAppInitializer(async () => {
      const tokenService = inject(AuthTokenService);
      const authSessionService = inject(AuthSessionService);
      const networkingSessionService = inject(NetworkingSessionService);
      const eventConfigState = inject(EventConfigStateService);

      try {
        const isAuthed = await firstValueFrom(tokenService.isAuthenticated$());
        if (!isAuthed) {
          return;
        }

        const email = await firstValueFrom(authSessionService.getUserEmail$());
        if (email) {
          networkingSessionService.syncShowWinnersIdentity(
            email,
            eventConfigState.$eventIdentifier() || undefined
          );
        }
      } catch {
        /* no Cognito session — email-dialog flow handles x-user headers */
      }
    }),
    provideAppInitializer(() => {
      const initializerFn = appIconsInitializer();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appMixpanelInitializer();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appRouteTrackingInitializer();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appScrollTrackingInitializer();
      return initializerFn();
    }),
    provideAnimationsAsync(),
    provideTranslocoConfig(),
  ],
};
